/* The feature switches. OWNED BY ITEM 6 OF docs/plan/00-prestavba-2026-08.md.

   These are server state. Switching one off switches it off for everybody, not
   for the browser that clicked — which is the entire reason they left
   localStorage. */

import { useEffect, useState } from "react";
import { api, ApiError, can, type Me } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import type { Settings } from "../../lib/types";

/* The defaults arrive with the settings. They used to be copied into this file,
   where the copy went stale the moment a feature was added — qr was missing
   from it, so the qr switch never said it differed from its default. */

export function Switches({ me }: { me: Me }) {
  const { ui } = useI18n();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { api<Settings>("/settings").then(setSettings).catch(() => setSettings(null)); }, []);

  async function toggle(id: string, value: boolean) {
    setBusy(id);
    setMsg("");
    try {
      setSettings(await api<Settings>("/settings", {
        method: "PATCH",
        body: JSON.stringify({ features: { [id]: value } }),
      }));
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : ui("admin.saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  if (!settings) return null;
  const defaults = settings.defaults ?? {};
  const delegated = settings.delegated ?? [];
  const changed = Object.entries(settings.features).filter(([id, on]) => on !== defaults[id]);
  /* Which switches this account may flip. The server refuses the rest whatever
     the page draws; showing them disabled says which they are and why. */
  const mayFlip = (id: string) =>
    can(me, "switches.all") || delegated.includes(id);

  return (
    <section className="adm-panel">
      {changed.length ? (
        <p className="adm-banner">
          {ui("admin.changedFromDefault")} {changed.map(([id]) => ui(`feature.${id}`)).join(", ")}.
        </p>
      ) : null}

      <ul className="adm-list">
        {Object.entries(settings.features).map(([id, on]) => (
          <li key={id} className={"adm-row" + (on ? " on" : "")}>
            <div className="adm-head">
              <span className="adm-name">{ui(`feature.${id}`)}</span>
              <button type="button" className="adm-switch" aria-pressed={on}
                      disabled={busy === id || !mayFlip(id)}
                      onClick={() => void toggle(id, !on)}>
                {on ? ui("admin.on") : ui("admin.off")}
              </button>
            </div>
            <p className="adm-d">{ui(`feature.${id}.d`)}</p>
            {on !== defaults[id] ? (
              <p className="adm-diff">
                {ui("admin.default")} {defaults[id] ? ui("admin.on") : ui("admin.off")}.
              </p>
            ) : null}
            {mayFlip(id) ? null : <p className="adm-diff">{ui("admin.adminOnly")}</p>}
          </li>
        ))}
      </ul>
      {msg ? <p className="adm-msg bad">{msg}</p> : null}
    </section>
  );
}
